import {LoadingState} from '../services/loading.service';
import {splitWorkflowTag, toDisplayText} from './ta-comment.util';
import {Type} from '@angular/core';
import {
  ApprovalContext,
  AuditLogEventDetails,
  BasicTaDetails,
  ItineraryItemDetails,
  LocationOption,
  ProjectDetails,
  SearchTaType,
  SubmissionApprovalDetails,
  TaWorkflowType,
  TAStatus,
  UserDetails,
  UserTravelItinerary
} from '../api';

export type StyleMap = Partial<Record<keyof CSSStyleDeclaration, string | number>>;

export type LoadingConfig = Omit<LoadingState, 'overlayStyle' | 'targetElement' | 'showSpinner'>

export enum CreateTaStepType {
  DETAILS = 'details',
  TRAVELLERS = 'travellers',
  ITINERARY = 'itinerary',
  DOCUMENTATION = 'documentation',
  REVIEW = 'review',
}

export interface CreateTaStep {
  title: string;
  subtitle?: string;
  code: CreateTaStepType;
  component: Type<any> | null;
  enabled?: boolean;
  valid?: boolean;
}

export interface ValidatorResponse {
  message: string;
  isValid: boolean;
}

export interface BreadCrumbData {
  title: string;
  titleIcon: string;
  section: string;
  breadTrail: string[];
  // Set by TA pages to opt into showing the current TA number in the title, e.g. 'Manage '
  taNumberPrefix?: string;
  // Set by the create, review and manage pages to opt into the TA Comments pill in the bar. Those
  // screens have nowhere else to hang it; the dashboards draw the same chain inline in the TA info
  // card instead, so they leave this off.
  showTaComments?: boolean;
  // Opt-in for the "Saving..." indicator, set by the pages that actually autosave (create and
  // review TA). The queue is app-wide, so without this the indicator would fire on any screen
  // whose actions happen to queue.
  showSaveStatus?: boolean;
  // Set by the template pages: warn-tinted bar plus the "editing a Template" warning.
  templateMode?: boolean;
}

export interface SearchTaList {
  totalCount?: number;
  taList?: BasicTaDetails[];
  currentCount?: number;
  loadedAt?: number; // Epoch ms of the page-0 fetch that built this cache.
  pagesLoaded?: number; // How many pages have been fetched into this cache and the next index to request.
}

// Mirrors TAStatusExtensions.ActiveStatuses / ClosedStatuses in the server's Model/TAStatus.cs.
export const ACTIVE_TA_STATUSES: TAStatus[] = [
  TAStatus.InProgress, TAStatus.PendingApproval, TAStatus.TravelApproved,
  TAStatus.PendingAcquittal, TAStatus.AcquittalComplete, TAStatus.AcquittalApproved,
  TAStatus.PendingCancelApproval, TAStatus.CancelApproved,
];

export const CLOSED_TA_STATUSES: TAStatus[] = [
  TAStatus.Finalised, TAStatus.Cancelled, TAStatus.TravelRejected,
];

/**
 * What a host lets the user do to a TA's comment chain. ta-comments draws and enforces these and
 * works none of them out itself, so what closes a conversation stays with whoever knows the TA -
 * status today, and whatever else a host has to weigh later.
 */
export interface TaCommentCapabilities {
  adding: boolean;
  editing: boolean;
  deleting: boolean;
}

/** Everything allowed - the component's default, and the state most TAs are in. */
export const ALL_TA_COMMENT_CAPABILITIES: TaCommentCapabilities = {
  adding: true,
  editing: true,
  deleting: true,
};

/**
 * The status rule the TA screens hand the chain, mirroring what the server refuses: a closed TA
 * takes no more edits or removals, and a deleted one takes nothing at all. A status the host has
 * not resolved yet is not a "no" - it leaves the controls showing, and the server is still the one
 * that decides.
 */
export function taCommentCapabilitiesFor(
  status: TAStatus | undefined | null,
): TaCommentCapabilities {
  if (status === TAStatus.Deleted) {
    return {adding: false, editing: false, deleting: false};
  }
  if (status && CLOSED_TA_STATUSES.includes(status)) {
    return {adding: true, editing: false, deleting: false};
  }
  return ALL_TA_COMMENT_CAPABILITIES;
}

// The two sections of the My Delegate TAs tab.
export type DelegateTaMode = 'active' | 'closed';

// FullUserDetails.approverType, set by the server in ObjectMapper.BuildFullUserDetails. A single
// precedence value rather than a set: managing or approving for a unit wins over approving for a
// project, which wins over merely having direct reports.
export type ApproverType = 'line' | 'project' | 'unit';

const APPROVER_TYPES: ApproverType[] = ['line', 'project', 'unit'];

export function toApproverType(value: string | null | undefined): ApproverType | undefined {
  return APPROVER_TYPES.find(type => type === value);
}

// The scope-based tabs on the approvals dashboard. These are the ?scope= values /user/approvals
// accepts beyond the default "line" - "unit" is the projects of units the user manages or approves
// for, "project" is the projects the user approves directly.
export type ApproverScope = 'unit' | 'project';

// The two unit manager tabs on the manager dashboard.
export type UnitTaMode = 'active' | 'closed';

// The three paged manager dashboard tabs served by manager-ta-list. Direct Reports' TAs is not here:
// it stays inline on the page because it carries the export link.
export type ManagerTaListSource = 'directReportHistory' | 'unitActive' | 'unitClosed';

// Compliance dashboard tabs the server lists; 'allSubmissions' does its own searching.
export type ComplianceDashboardTab = 'overpaid' | 'dayTrips';

// Home page tabs. Carried in the URL as ?tab= so returning from a TA lands on the tab you left from.
export type HomeTab = 'inProgress' | 'delegate' | 'history' | 'templates';

const HOME_TABS: HomeTab[] = ['inProgress', 'delegate', 'history', 'templates'];

export function toHomeTab(value: string | null | undefined): HomeTab | undefined {
  return HOME_TABS.find(tab => tab === value);
}



export interface ApprovalWorkflowStage {
  label: string;
  context: ApprovalContext;
  approval: SubmissionApprovalDetails;
  status: ApprovalStatusLabel;
  severity: ApprovalStatusSeverity;
  approverName: string;
  actionedDate?: string | null;
}

// A lifecycle step can hold more than one approval - a withheld TA that is resubmitted keeps the
// actioned approval and gains a new pending one. Stages that show the full history carry this flag.
export interface ApprovalWorkflowHistoryStage extends ApprovalWorkflowStage {
  // The newest approval for its lifecycle step, and the only one that can be overridden.
  isLatest: boolean;
}

export type ApprovalStatusLabel = 'Approved' | 'Withheld' | 'Rejected' | 'Pending' | 'Overdue' | 'Processed' | 'Cancelled';

// PrimeNG has no 'primary' badge severity - the default (undefined) badge is already the primary colour.
export type ApprovalStatusSeverity = 'success' | 'danger' | 'secondary' | 'warn' | 'info' | undefined;

export interface ApprovalStatusBadge {
  label: ApprovalStatusLabel;
  severity: ApprovalStatusSeverity;
}

export const ApprovalActionInfo: Record<string, ApprovalStatusBadge> = {
  'Approve': {label: 'Approved', severity: 'success'},
  'Withhold': {label: 'Withheld', severity: 'info'},
  'Reject': {label: 'Rejected', severity: 'danger'},
  'Processed': {label: 'Processed', severity: 'info'},
  'Cancelled': {label: 'Cancelled', severity: 'danger'},
  'Overdue': {label: 'Overdue', severity: 'warn'},
  'Pending': {label: 'Pending', severity: 'secondary'},
}

export interface FinanceSearchState {
  advancedEnabled: boolean;
  basicSearchStr: string;
  advancedSearchParameters: AdvancedSearchParameters;
  currentPage: number;
  results: SearchTaList | undefined;
  hasSearched: boolean;
}

export interface AdvancedSearchParameters {
  taStatusList?: TAStatus[];
  projectList?: ProjectDetails[];
  onlyPrimaryTraveller?: boolean;
  travellerList?: UserDetails[];
  approverList?: UserDetails[];
  createdByList?: UserDetails[];
  locationList?: LocationOption[];
  dateStart?: Date;
  dateEnd?: Date;
  taTypeList?: SearchTaType[];
  purposeList?: string[];
}

export function emptyAdvancedSearchParameters(): AdvancedSearchParameters {
  return {
    taStatusList: [],
    onlyPrimaryTraveller: false,
    projectList: [],
    travellerList: [],
    approverList: [],
    createdByList: [],
    locationList: [],
    dateStart: undefined,
    dateEnd: undefined,
    taTypeList: [],
    purposeList: [],
  };
}

export function toTAStatusText(code: keyof typeof TAStatuses | string): string {
  if (code in TAStatuses) {
    return TAStatuses[code as keyof typeof TAStatuses];
  }
  return code;
}

export const TAStatuses = {
  InProgress: 'In Progress',
  PendingApproval: 'Pending Approval',
  TravelApproved: 'Travel Approved',
  TravelRejected: 'Travel Rejected',
  PendingAcquittal: 'Pending Acquittal',
  PendingCancelApproval: 'Pending Cancel',
  AcquittalComplete: 'Acquittal Complete',
  AcquittalApproved: 'Ready For Payment',
  CancelApproved: 'Cancel Approved',
  Finalised: 'Finalised',
  Cancelled: 'Cancelled',
  Deleted: 'Deleted'
} as const;

export const MealTypes = {
  Breakfast: 'Breakfast',
  Lunch: 'Lunch',
  Dinner: 'Dinner',
  Camping: 'Camping',
  Overseas: 'Overseas',
  Incidentals: 'Incidentals',
  DayTrip: 'DayTrip',
} as const;

export function toEventTypesText(code: keyof typeof EventTypes | string): string {
  if (code in EventTypes) {
    return EventTypes[code as keyof typeof EventTypes];
  }
  return code;
}

export function toEventTypeDisplayText(event: AuditLogEventDetails): string {
  if (event.eventType === 'ApprovalAction' && event.metadata) {
    const action = event.metadata['approvalAction'];
    const context = event.metadata['approvalContext'];

    if (action === 'Withhold') {
      if (context === 'Acquittal') return 'Acquittal Withheld';
      if (context === 'SecondaryAcquittal') return 'Acquittal Withheld (Funding)';
      return 'Withheld'
    }
    if (action === 'Reject'){

    }
    if (action === 'Approve') {
      if (context === 'OutOfRegion') return 'Approved (Out of Region)';
      if (context === 'Funding') return 'Approved (Funding)';
      if (context === 'Acquittal') return 'Acquittal Approved';
      if (context === 'SecondaryAcquittal') return 'Acquittal Approved (Funding)';
      if (context === 'PrePayment') return 'Pre-Payment Processed';
      if (context === 'OverPayment') return 'Over-Payment Processed';
      if (context === 'DayTripPayment') return 'Day Trip Payment Processed';
      return 'Approved';
    }
    if (action === 'Processed') {
      if (context === 'PrePayment') return 'Pre-Payment Processed';
      if (context === 'OverPayment') return 'Over-Payment Processed';
      if (context === 'DayTripPayment') return 'Day Trip Payment Processed';
      return `Processed ${context}`;
    }
    if (action === 'Cancelled') {
      if (context === 'PrePayment') return 'Pre-Payment Cancelled';
      return 'Cancelled';
    }
  }

  if (event.eventType === 'SubmissionStatusChange' && event.metadata) {
    const oldStatus = event.metadata['oldStatus'];
    const newStatus = event.metadata['newStatus'];
    if (oldStatus && newStatus) {
      return `Status Change: ${toTAStatusText(oldStatus)} > ${toTAStatusText(newStatus)}`;
    }
  }

  if (event.eventType === 'FileEvent' && event.metadata) {
    const action = event.metadata['action'];
    const file = event.metadata['file'];
    if (action === 'upload') return `File Upload: ${file}`;
    if (action === 'delete') return `File Deleted: ${file}`;
  }

  if (event.eventType === 'CommentEvent' && event.metadata) {
    const action = event.metadata['action'];
    if (action === 'add') return 'Comment Added';
    if (action === 'edit') return 'Comment Edit';
    if (action === 'delete') return 'Comment Deleted';
  }

  return toEventTypesText(event.eventType ?? '');
}

export const EventTypes = {
  UserLogon: 'User Logged In',
  CreateTaSubmission: 'Created',
  SubmitTaSubmission: 'Submitted',
  ApprovalAction: 'Approved',
  SubmissionStatusChange: 'Submission Status Change',
  TravelAcquitted: 'Acquitted',
  JobStarted: 'Job Started',
  JobCompleted: 'Job Completed',
  JobFailed: 'Job Failed',
  FileEvent: 'File Event',
  CommentEvent: 'Comment',
  CreateTaTemplate: 'Create Template',
  SubmissionEdit: 'Details Edited',
  ItineraryEdit: 'Itinerary Edited',
  DeleteTaTemplate: 'Delete Template'
} as const;

export function toEventMetaTypesText(code: keyof typeof EventMetaTypes | string): string {
  if (code in EventMetaTypes) {
    return EventMetaTypes[code as keyof typeof EventMetaTypes];
  }
  return code;
}

export const EventMetaTypes = {
  taNumber: 'TA Number',
  oldStatus: 'Old Status',
  newStatus: 'New Status',
  comment: 'Comment',
  previousComment: 'Previous Comment',
  approvalAction: 'Approval Action',
  approvalContext: 'Approval Context',
  action: 'Action',
  itemDate: 'Item Date',
  travellerEmail: 'Traveller Email',
  travellerUserId: 'Traveller User Id',
  estimate: 'Estimate',
  isPrepaid: 'Is Prepaid',
  prepaymentProcessed: 'Prepayment Processed',
  travellerCount: 'Traveller Count',
  primaryTravellerEmail: 'Primary Traveller',
  isDelegateTa: 'Is Delegate TA',
  fromTemplate: 'From Template',
  templateId: 'Template Id',
  templateName: 'Template Name',
  workflowType: 'Workflow Type',
  prepayAmount: 'Prepay Amount',
  'change:IsManual': 'Manual Override',

} as const;

export function toWorkflowTagText(code: keyof typeof WorkflowTags | string): string {
  if (code in WorkflowTags) {
    return WorkflowTags[code as keyof typeof WorkflowTags];
  }
  return code;
}

/** Labels for the `#[workflow](key)` tag on a workflow-note comment - see splitWorkflowTag. */
export const WorkflowTags = {
  'submit': 'At Submission',
  'approve': 'At Approval',
  'withhold': 'At Approval',
  'reject': 'At Approval',
  'acquit': 'At Acquittal',
  'acquittal-approve': 'At Acquittal',
  'acquittal-withhold': 'At Acquittal',
  'cancel': 'At Cancellation',
  'itinerary-note': 'Itinerary note',
} as const;

/** Stored comment text as the audit log shows it: `Label: body`, mentions flattened to `@Name`. */
export function toCommentAuditText(text: string | null | undefined): string {
  const {key, body} = splitWorkflowTag(text);
  const display = toDisplayText(body);
  return key ? `${toWorkflowTagText(key)}: ${display}` : display;
}

/**
 * One metadata value as it should read: the comment keys hold stored comment text, so a workflow
 * tag becomes its label and mentions are flattened from `@[Name](id)` to `@Name`; a status code is
 * named; anything else stands as it is, `toTAStatusText` passing an unrecognised code straight through.
 */
export function toEventMetaValueText(
  code: keyof typeof EventMetaTypes | string,
  value: string | undefined,
): string {
  if (code === 'comment' || code === 'previousComment') {
    return toCommentAuditText(value);
  }
  // Stored as `old::new` Yes/No
  if (code === 'change:IsManual') {
    return value?.endsWith('::Yes') ? 'Turned On' : 'Turned Off';
  }
  return toTAStatusText(value ?? '');
}

export function toInitialTypeText(code: keyof typeof InitialTypes | string): string {
  if (code in InitialTypes) {
    return InitialTypes[code as keyof typeof InitialTypes];
  }
  return code;
}

export const InitialTypes = {
  OutOfRegion: 'Out Of Region',
  Funding: 'Funding',
  Acquittal: 'Acquittal',
  SecondaryAcquittal: 'Funding Acquittal',
  Manager: 'Manager',
} as const;

export function toApprovalTypeText(code: keyof typeof ApprovalAction | string): string {
  if (code in ApprovalAction) {
    return ApprovalAction[code as keyof typeof ApprovalAction];
  }
  return code;
}

export const ApprovalAction = {
  Approve: 'Approved',
  Withhold: 'Withheld',
  Reject: 'Rejected',
  Cancelled: 'Cancelled'
} as const;

export function isFlightOnlyTa(ta: Pick<BasicTaDetails, 'workflowType'> | null | undefined): boolean {
  return ta?.workflowType === TaWorkflowType.FlightOnly;
}

export type FlightTripType = 'oneWay' | 'return';

/** One-way is stored as start == end; null until both dates exist. */
export function flightTripTypeFromDates(
  start: string | null | undefined,
  end: string | null | undefined,
): FlightTripType | null {
  if (!start || !end) {
    return null;
  }
  return start.substring(0, 10) === end.substring(0, 10) ? 'oneWay' : 'return';
}

export type TaCardAction = 'travelApprove' | 'acquittalApprove' | 'cancelApprove'
  | 'finalise' | 'cancelFinalise' | 'prepayment' | 'dayTripPayment';

export interface ItineraryItemDetailsExtended extends ItineraryItemDetails {
  startDate?: Date;
  endDate?: Date;
  startLocation?: LocationOption
  endLocation?: LocationOption;
  supportingInfoLabel? : string;
  allowedFields?: string[]; //breakfast, lunch, dinner, camping, overseas
  isOverseas?: boolean; // start or end location is overseas
}

export interface UserTravelItineraryExtended extends UserTravelItinerary {
  items: ItineraryItemDetailsExtended[];
  disabledDates?: Date[];
}

export interface ItineraryDiffItemDetails extends ItineraryItemDetails {
  dateChangedClass?: string;
  fieldChanged: Partial<Record<keyof ItineraryItemDetails, string>>;
}

export function toLocationCategoriesText(code: keyof typeof LocationCategories | string): string {
  if (code in LocationCategories) {
    return LocationCategories[code as keyof typeof LocationCategories];
  }
  return code;
}

export const LocationCategories = {
  TownOrCity: 'Town or City',
  Country: 'Country',
  Overseas: 'Overseas',
} as const;

/** The three states of the prepaid indicator shown on the finance dashboard tables. */
export type PrepaidIndicator = 'none' | 'prepaid' | 'overpaid';

/**
 * A value shortfall only means an overpayment once an acquittal actually exists. Before that,
 * currentValue sitting under originalValue just reflects an in-flight estimate revision.
 */
export const ACQUITTED_STATUSES: TAStatus[] = [
  TAStatus.AcquittalComplete,
  TAStatus.AcquittalApproved,
  TAStatus.Finalised,
];

/**
 * The cancellation lifecycle of a pre-paid TA whose prepayment had already been processed - the
 * only way into either status. Mirrors TAStatusExtensions.IsCancelFlow on the server.
 *
 * The two are not interchangeable where value is concerned. The itinerary is zeroed by
 * TAStore.ApproveCancellationSubmission, not by the cancel request, so a PendingCancelApproval TA
 * is still worth its full value and only a CancelApproved one reads $0. Use this constant for
 * "a cancellation is under way" questions; test the individual status for value questions.
 */
export const CANCEL_FLOW_STATUSES: TAStatus[] = [
  TAStatus.PendingCancelApproval,
  TAStatus.CancelApproved,
];

// Statuses that can't be saved as a template. Mirrors TemplateStore.CreateFromSubmission on the server.
export const NON_TEMPLATABLE_STATUSES: TAStatus[] = [
  TAStatus.TravelRejected,
  TAStatus.Cancelled,
  TAStatus.Deleted,
  ...CANCEL_FLOW_STATUSES,
];

// Unknown status → true; the server decides. Flight-only TAs are never templated.
export function canCreateTemplateFrom(ta: Pick<BasicTaDetails, 'status' | 'workflowType'> | null | undefined): boolean {
  if (isFlightOnlyTa(ta)) {
    return false;
  }
  const status = ta?.status;
  return !status || !NON_TEMPLATABLE_STATUSES.includes(status);
}

// Statuses a prepayment can be cancelled in - mirrors TAStore.PrePaidDashboardStatuses.
export const PREPAYMENT_CANCELLABLE_STATUSES: TAStatus[] = [
  TAStatus.TravelApproved,
  TAStatus.PendingAcquittal,
  TAStatus.AcquittalComplete,
  TAStatus.AcquittalApproved
];
